import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlayIcon } from "lucide-react";
import type { Film } from "../../types/film";
import { ReelModal } from "../hero/ReelModal";
interface LandscapeCardProps {
  film: Film;
  first?: boolean;
  last?: boolean;
  expand?: boolean;
}
const HOVER_DELAY = 250;
export function LandscapeCard({
  film,
  first,
  last,
  expand = true
}: LandscapeCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timer = useRef<number>();
  const [previewing, setPreviewing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const startPreview = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setPreviewing(true);
      videoRef.current?.play().catch(() => undefined);
    }, HOVER_DELAY);
  };
  const stopPreview = () => {
    window.clearTimeout(timer.current);
    setPreviewing(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };
  const ytPreview = film.youtubeId ? `https://www.youtube-nocookie.com/embed/${film.youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${film.youtubeId}&modestbranding=1&playsinline=1&rel=0&disablekb=1` : "";
  return <article className={`film-card group/card relative ${expand ? "" : "is-static"} ${first ? "origin-left" : last ? "origin-right" : "origin-center"}`} onMouseEnter={startPreview} onMouseLeave={stopPreview} onFocus={startPreview} onBlur={stopPreview} data-id="element-20" data-mp="6dir6xlitzc7">
      <div className="film-media relative aspect-video overflow-hidden bg-card" data-id="element-21" data-mp="bddgiwpnegv5">
        <img src={film.image} alt={film.alt} loading="lazy" className="h-full w-full object-cover brightness-[0.6] transition-[filter] duration-200 ease-out group-hover/card:brightness-100 group-focus-within/card:brightness-100" onError={e => {
        // YouTube doesn't always provide a max-res thumbnail.
        if (film.youtubeId && !e.currentTarget.src.includes("hqdefault")) {
          e.currentTarget.src = `https://i.ytimg.com/vi/${film.youtubeId}/hqdefault.jpg`;
        }
      }} data-id="element-22" data-mp="bbajvttzpphg" />

        {film.videoSrc && <video ref={videoRef} src={film.videoSrc} muted loop playsInline preload="none" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${previewing ? "opacity-100" : "opacity-0"}`} data-id="element-23" data-mp="zrtipj7gkwdu" />}

        {film.youtubeId && previewing && <iframe src={ytPreview} title={`${film.title} — muted preview`} allow="autoplay; encrypted-media" tabIndex={-1} aria-hidden="true"
      // Overscaled to crop YouTube's title bar and edges.
      className="pointer-events-none absolute left-1/2 top-1/2 h-[135%] w-[135%] -translate-x-1/2 -translate-y-1/2 border-0" data-id="element-24" data-mp="1dpi0uja55pf7" />}

        <span className="absolute right-3 top-3 bg-black/50 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-white/80" data-id="element-25" data-mp="4r4blhyl0qfh">
          {film.aspect}
        </span>
      </div>
      <div className="film-panel bg-card px-4 pb-4 pt-3" data-id="element-26" data-mp="3vx5gt9vv8pt7">
        <div className="flex items-end justify-between gap-3" data-id="element-27" data-mp="3qyklnovh0wbm">
          <div className="min-w-0" data-id="element-28" data-mp="3lz7q8fqjagxf">
            <h3 className="truncate font-serif text-[20px] leading-tight text-white" data-id="element-29" data-mp="3ly6f6b9ipufp">{film.title}</h3>
            <p className="mt-0.5 flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] text-body" data-id="element-30" data-mp="3hynspb21tbar">
              <span className="truncate" data-id="element-31" data-mp="3czupqcke12ou">{film.meta}</span>
              <span className="shrink-0 bg-signal-tint px-1.5 py-0.5 text-signal" data-id="element-32" data-mp="1g5zmcsu3y7qr">{film.tag}</span>
            </p>
          </div>
          {film.youtubeId && <button type="button" aria-label={`Watch ${film.title} with sound`} onClick={() => setModalOpen(true)} className="flex shrink-0 items-center gap-1.5 pb-0.5 font-mono text-[11px] tracking-[0.12em] text-white/80 transition-colors duration-150 hover:text-signal" data-id="element-33" data-mp="1b7y8cmbjgym5">
              <PlayIcon className="h-3 w-3 fill-current" aria-hidden="true" data-id="element-34" data-mp="3u5uazwhsgkn" />
              WATCH
            </button>}
        </div>
      </div>

      {film.youtubeId && <ReelModal open={modalOpen} onClose={closeModal} videoId={film.youtubeId} title={film.title} data-id="element-35" data-mp="3l9ca5el8h08p" />}
    </article>;
}